/**
 * Admin — per-user access editor. Searchable event list with a toggle each,
 * granted events shown first. Scales to many events (only one user's worth of
 * toggles render). Admin-only.
 */
import { useMemo, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { IconArrowLeft, IconSearch, IconCheck, IconMoodSad } from '@tabler/icons-react';
import { useAuthStore } from '../state/authStore.ts';
import { useEvents } from '../hooks/useEvents.ts';
import { adminGetUser, adminSetAccess } from '../lib/apiClient.ts';
import { Spinner } from '../components/ui/Spinner.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { toast } from '../state/toastStore.ts';
import { cn } from '../lib/cn.ts';

export function AdminUserPage() {
  const me = useAuthStore((s) => s.me);
  const { email = '' } = useParams();
  const decodedEmail = decodeURIComponent(email);
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState('');
  const [pending, setPending] = useState<Set<string>>(new Set());

  const { data: events } = useEvents();
  const {
    data: user,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['admin-user', decodedEmail],
    queryFn: ({ signal }) => adminGetUser(decodedEmail, signal),
  });

  const granted = useMemo(() => new Set(user?.events ?? []), [user]);
  const isAdminUser = user?.role === 'admin';

  // Event catalog, granted-first then alphabetical, filtered by search.
  const eventNames = useMemo(() => {
    const names = (events ?? []).map((e) => e.name);
    const q = filter.trim().toLowerCase();
    return names
      .filter((n) => !q || n.toLowerCase().includes(q))
      .sort((a, b) => {
        const ga = granted.has(a) ? 0 : 1;
        const gb = granted.has(b) ? 0 : 1;
        return ga - gb || a.localeCompare(b);
      });
  }, [events, filter, granted]);

  if (me && me.role !== 'admin') return <Navigate to="/events" replace />;

  const toggle = async (ev: string) => {
    if (isAdminUser) return;
    const has = granted.has(ev);
    setPending((p) => new Set(p).add(ev));
    try {
      await adminSetAccess({ email: decodedEmail, event: ev, granted: !has });
      await queryClient.invalidateQueries({ queryKey: ['admin-user', decodedEmail] });
      await queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      // Granting access clears any pending request for this user server-side.
      if (!has) await queryClient.invalidateQueries({ queryKey: ['admin-requests'] });
    } catch {
      toast.error('Couldn’t update access');
    } finally {
      setPending((p) => {
        const n = new Set(p);
        n.delete(ev);
        return n;
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link
          to="/admin"
          className="grid h-9 w-9 place-items-center rounded-xl border border-line bg-surface text-fg-2 shadow-sm hover:bg-grid"
          aria-label="Back to users"
        >
          <IconArrowLeft className="h-5 w-5" />
        </Link>
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-bold text-fg">{user?.name || decodedEmail}</h1>
          <p className="truncate text-sm text-fg-2">{decodedEmail}</p>
        </div>
      </div>

      {isLoading && (
        <div className="flex justify-center py-16 text-accent">
          <Spinner className="h-7 w-7" />
        </div>
      )}
      {isError && <EmptyState icon={IconMoodSad} title="Couldn't load this user" />}

      {user && isAdminUser && (
        <div className="rounded-xl border border-line bg-accent-bg/40 p-4 text-sm text-fg-2">
          This user is an <span className="font-semibold text-accent">admin</span> — they have
          access to all events. Change their role to a member to manage per-event access.
        </div>
      )}

      {user && !isAdminUser && (
        <>
          <div className="relative max-w-md">
            <IconSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
            <input
              type="search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter events…"
              aria-label="Filter events"
              className="h-10 w-full rounded-xl border border-line bg-surface pl-9 pr-3 text-sm outline-none placeholder:text-muted"
            />
          </div>

          <div className="overflow-hidden rounded-2xl border border-line bg-surface shadow-sm">
            <ul className="divide-y divide-line">
              {eventNames.map((ev) => {
                const has = granted.has(ev);
                const busy = pending.has(ev);
                return (
                  <li key={ev} className="flex items-center justify-between gap-3 px-4 py-2.5">
                    <span className="truncate text-sm text-fg">{ev}</span>
                    <button
                      onClick={() => toggle(ev)}
                      disabled={busy}
                      role="switch"
                      aria-checked={has}
                      aria-label={`${has ? 'Revoke' : 'Grant'} access to ${ev}`}
                      className={cn(
                        'inline-flex h-7 items-center gap-1.5 rounded-full px-3 text-xs font-medium transition duration-200 ease-standard',
                        has
                          ? 'bg-accent text-white'
                          : 'border border-line-2 text-fg-2 hover:border-accent',
                        busy && 'opacity-60',
                      )}
                    >
                      {busy ? (
                        <Spinner className="h-3 w-3" />
                      ) : has ? (
                        <>
                          <IconCheck className="h-3.5 w-3.5" /> Granted
                        </>
                      ) : (
                        'Grant'
                      )}
                    </button>
                  </li>
                );
              })}
              {eventNames.length === 0 && (
                <li className="px-4 py-6 text-center text-sm text-muted">No matching events.</li>
              )}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
